import { useEffect, useMemo, useState } from "react";
import { History, Filter, X, User, FileText, Boxes, ClipboardList, ScrollText, LogIn, Activity, CreditCard, MessageSquareWarning, Download } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";

const ACTION_CATEGORIES = [
  { key: "All", match: () => true },
  { key: "Login", match: (a) => /logged in/i.test(a) },
  { key: "Contract", match: (a) => /contract/i.test(a) },
  { key: "Quote", match: (a) => /quote/i.test(a) },
  { key: "Inventory", match: (a) => /inventory/i.test(a) },
  { key: "Checkout", match: (a) => /checked (out|back)|items|inventory adjusted/i.test(a) },
  { key: "Mockup", match: (a) => /mockup/i.test(a) },
  { key: "Payment", match: (a) => /paid|deposit|balance|payment/i.test(a) },
  { key: "Employee", match: (a) => /employee/i.test(a) },
  { key: "Status", match: (a) => /status|→/i.test(a) },
];

function iconFor(action = "") {
  const a = action.toLowerCase();
  if (a.includes("logged in")) return LogIn;
  if (a.includes("checked out") || a.includes("checked back") || a.includes("items")) return ClipboardList;
  if (a.includes("inventory")) return Boxes;
  if (a.includes("mockup")) return ScrollText;
  if (a.includes("quote")) return FileText;
  if (a.includes("contract")) return FileText;
  if (a.includes("paid") || a.includes("payment") || a.includes("deposit") || a.includes("balance")) return CreditCard;
  if (a.includes("employee")) return User;
  if (a.includes("change") || a.includes("requested")) return MessageSquareWarning;
  return Activity;
}

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminActivity() {
  const [rows, setRows] = useState([]);
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actor, setActor] = useState("All");
  const [category, setCategory] = useState("All");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actor !== "All") params.set("actor", actor);
      if (since) params.set("since", new Date(since + "T00:00:00").toISOString());
      if (until) params.set("until", new Date(until + "T23:59:59").toISOString());
      params.set("limit", "1000");
      const r = await api.get(`/admin/activity?${params.toString()}`);
      setRows(r.data || []);
      // Actors dropdown is best-effort — older backends may not have it.
      try {
        const a = await api.get("/admin/activity/actors");
        setActors(a.data || []);
      } catch {
        // Fallback: derive distinct actors from the rows we already have.
        const derived = Array.from(new Set((r.data || []).map((row) => row.actor).filter(Boolean))).sort();
        setActors(derived);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load activity");
    }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [actor, since, until]);

  const filtered = useMemo(() => {
    const cat = ACTION_CATEGORIES.find((c) => c.key === category) || ACTION_CATEGORIES[0];
    return rows.filter((r) => cat.match(r.action || ""));
  }, [rows, category]);

  const clear = () => { setActor("All"); setCategory("All"); setSince(""); setUntil(""); };

  return (
    <div data-testid="admin-activity-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Audit</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Activity Log</h1>
          <p className="text-white/50 mt-2 text-sm">{filtered.length} of {rows.length} entries</p>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/40 inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-2"><History size={12} /> Last 1000 actions across the app</span>
          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (actor !== "All") params.set("actor", actor);
              if (since) params.set("since", new Date(since + "T00:00:00").toISOString());
              if (until) params.set("until", new Date(until + "T23:59:59").toISOString());
              const url = `${API}/admin/activity.csv?${params.toString()}`;
              fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
                .then((r) => r.blob())
                .then((b) => {
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(b);
                  a.download = "activity-log.csv";
                  document.body.appendChild(a); a.click(); a.remove();
                })
                .catch(() => toast.error("Export failed"));
            }}
            data-testid="activity-export-csv"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/40 px-3 py-1.5 hover:bg-[#C9963F]/10"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#0e0e0e] border border-white/10 p-4 mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#C9963F] mb-3">
          <Filter size={12} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Actor</label>
            <select
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              data-testid="activity-filter-actor"
              className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white"
            >
              <option value="All">All people</option>
              {actors.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="activity-filter-category"
              className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white"
            >
              {ACTION_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.key}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">From</label>
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="activity-filter-since"
              className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white" />
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">To</label>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="activity-filter-until"
              className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white" />
          </div>
        </div>
        {(actor !== "All" || category !== "All" || since || until) && (
          <button onClick={clear} data-testid="activity-clear-filters"
            className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/50 hover:text-[#C9963F]">
            <X size={12} /> Clear all filters
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0e0e0e] border border-white/10 p-10 text-center text-white/40 text-sm">
          No activity matches these filters.
        </div>
      ) : (
        <div className="bg-[#0e0e0e] border border-white/10 divide-y divide-white/5" data-testid="activity-list">
          {filtered.map((row, idx) => {
            const Icon = iconFor(row.action);
            const isEmployee = row.actor && row.actor !== "Admin";
            return (
              <div key={`${row.created_at}-${idx}`} className="px-4 py-3 flex items-start gap-3" data-testid={`activity-row-${idx}`}>
                <div className={`mt-0.5 p-1.5 border ${isEmployee ? "border-[#C9963F]/40 text-[#C9963F]" : "border-white/15 text-white/60"}`}>
                  <Icon size={14} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{row.action}</div>
                  <div className="text-xs text-white/50 mt-0.5 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider border ${isEmployee ? "border-[#C9963F]/40 text-[#C9963F]" : "border-white/15 text-white/60"}`}>
                      <User size={9} /> {row.actor || "Admin"}
                    </span>
                    {row.client_name && <span className="text-white/70">{row.client_name}</span>}
                    {row.detail && <span className="text-white/50 truncate">· {row.detail}</span>}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/40 shrink-0 text-right">
                  {fmt(row.created_at)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
